import { useMemo, useState, useCallback } from 'react';
import { useMutation, useQuery, useApolloClient } from '@apollo/client/react';
import {
  IonItem,
  IonLabel,
  IonIcon,
  IonAlert,
  IonSpinner,
  useIonToast,
  useIonRouter,
} from '@ionic/react';
import { refresh, trash, keyOutline, settingsOutline, closeOutline, personRemoveOutline, shieldCheckmarkOutline, shareSocialOutline } from 'ionicons/icons';
import { Share } from '@capacitor/share';

import { posthog } from '../../../../lib/posthog';
import { RESET_TEAMS, DELETE_LEAGUE } from '../../../../graphql/mutations/leagueAdmin';
import { UPDATE_FANTASY_LEAGUE } from '../../../../graphql/mutations/updateFantasyLeague';
import { GET_FANTASY_LEAGUE } from '../../../../graphql/queries/fantasyLeague';
import { GET_MY_FANTASY_LEAGUES } from '../../../../graphql/queries/myFantasyLeagues';

import type {
  ResetTeamsMutation,
  ResetTeamsMutationVariables,
  DeleteLeagueMutation,
  DeleteLeagueMutationVariables,
  UpdateFantasyLeagueMutation,
  UpdateFantasyLeagueMutationVariables,
  GetFantasyLeagueQuery,
  GetFantasyLeagueQueryVariables,
} from '../../../../types/graphql-generated';

import { SecretCodeModal } from './SecretCodeModal';
import { EditFantasyLeagueConfigModal } from './EditFantasyLeagueConfigModal';
import { ExpelMemberModal } from './ExpelMemberModal';
import { ManageAdminsModal } from './ManageAdminsModal';
import { DOWNLOAD_URL } from '../../../../appConfig';

interface LeagueAdminItemsProps {
  leagueId: string;
  currentMemberId?: string;
  currentMemberRole?: string;
}

type ToastKind = 'success' | 'danger';

// Admin-only items of the league settings list (rendered inside the page's
// IonList). Owns the league query, the admin mutations and their alerts/modals.
export const LeagueAdminItems = ({ leagueId, currentMemberId, currentMemberRole }: LeagueAdminItemsProps) => {
  const router = useIonRouter();
  const [presentToast] = useIonToast();
  const apolloClient = useApolloClient();

  const [showResetAlert, setShowResetAlert] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showEditFantasyLeagueConfig, setShowEditFantasyLeagueConfig] = useState(false);
  const [showSecretCode, setShowSecretCode] = useState(false);
  const [showExpelMember, setShowExpelMember] = useState(false);
  const [showManageAdmins, setShowManageAdmins] = useState(false);

  const showToast = useCallback(
    (kind: ToastKind, message: string) => {
      presentToast({
        message,
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'app-footer',
        cssClass: kind === 'success' ? 'toast-success' : 'toast-danger',
        buttons: [{ icon: closeOutline, role: 'cancel' }],
      });
    },
    [presentToast]
  );

  const { data, loading: leagueLoading } = useQuery<GetFantasyLeagueQuery, GetFantasyLeagueQueryVariables>(
    GET_FANTASY_LEAGUE,
    {
      variables: { id: leagueId },
    }
  );

  const league = data?.fantasyLeague;

  const initialConfig = useMemo(() => {
    if (!league) return null;
    return {
      name: league.name ?? '',
      maxPlayers: league.maxPlayers ?? null,
      isPublic: Boolean(league.isPublic),
    };
  }, [league]);

  const [resetTeams, { loading: resetLoading }] = useMutation<
    ResetTeamsMutation,
    ResetTeamsMutationVariables
  >(RESET_TEAMS, {
    onCompleted: async (res) => {
      const errors = res.resetTeams?.errors ?? [];
      if (errors.length === 0) {
        await apolloClient.resetStore();
        showToast('success', 'Liga reseteada correctamente');
      } else {
        showToast('danger', errors[0] ?? 'Error al resetear la liga');
      }
    },
    onError: (error) => showToast('danger', `Error: ${error.message}`),
  });

  const [updateFantasyLeague, { loading: updateLoading }] = useMutation<
    UpdateFantasyLeagueMutation,
    UpdateFantasyLeagueMutationVariables
  >(UPDATE_FANTASY_LEAGUE, {
    refetchQueries: [{ query: GET_FANTASY_LEAGUE, variables: { id: leagueId } }],
    onCompleted: (res) => {
      const errors = res.updateFantasyLeague?.errors ?? [];
      if (errors.length === 0) {
        showToast('success', 'Configuración actualizada correctamente');
        setShowEditFantasyLeagueConfig(false);
      } else {
        showToast('danger', errors[0] ?? 'Error al actualizar la configuración');
      }
    },
    onError: (error) => showToast('danger', `Error: ${error.message}`),
  });

  const [deleteLeague, { loading: deleteLoading }] = useMutation<
    DeleteLeagueMutation,
    DeleteLeagueMutationVariables
  >(DELETE_LEAGUE, {
    refetchQueries: [
      { query: GET_MY_FANTASY_LEAGUES },
    ],
    onCompleted: (res) => {
      if (res.deleteLeague?.success) {
        showToast('success', 'Liga eliminada correctamente');
        // Root navigation drops the deleted league's screens, so back can't return to them
        router.push('/app/main/mis-ligas', 'root', 'replace');
      } else {
        showToast('danger', res.deleteLeague?.errors?.[0] ?? 'Error al eliminar liga');
      }
    },
    onError: (error) => showToast('danger', `Error: ${error.message}`),
  });

  const handleResetTeams = useCallback(() => {
    resetTeams({ variables: { input: { leagueId } } });
  }, [resetTeams, leagueId]);

  const handleUpdateFantasyLeague = useCallback(
    (values: { name?: string | null; isPublic?: boolean | null; maxPlayers?: number | null }) => {
      updateFantasyLeague({
        variables: {
          input: {
            fantasyLeagueId: leagueId, // schema expects fantasyLeagueId
            name: values.name ?? null,
            isPublic: values.isPublic ?? null,
            maxPlayers: values.maxPlayers ?? null,
          },
        },
      });
    },
    [updateFantasyLeague, leagueId]
  );

  const handleDeleteLeague = useCallback(() => {
    deleteLeague({ variables: { input: { leagueId } } });
  }, [deleteLeague, leagueId]);

  const handleShareLeague = useCallback(async () => {
    if (!league) return;

    const accessCodeLine = league.secretCode?.trim()
      ? ` La clave de acceso es: ${league.secretCode}`
      : '';
    const text =
      `¡Únete a mi liga "${league.name}" en Futyclub!${accessCodeLine}\n\n` +
      `Descárgate la app desde ${DOWNLOAD_URL}`;

    try {
      const { value: canShare } = await Share.canShare();
      if (canShare) {
        await Share.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        showToast('success', 'Invitación copiada al portapapeles');
      }
      posthog.capture('league_shared_with_social_button');
    } catch {
      // The user dismissed the share sheet; nothing to report.
    }
  }, [league, showToast]);

  const isMutating = resetLoading || deleteLoading || updateLoading;

  return (
    <>
      {isMutating ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <IonSpinner name="crescent" />
        </div>
      ) : (
        <>
          {league?.isPublic ? null : (
            <IonItem button onClick={() => setShowSecretCode(true)}>
              <IonIcon icon={keyOutline} slot="start" className="mr-3" />
              <IonLabel>Código secreto</IonLabel>
            </IonItem>
          )}

          <IonItem button onClick={handleShareLeague}>
            <IonIcon icon={shareSocialOutline} slot="start" className="mr-3" />
            <IonLabel>Compartir liga</IonLabel>
          </IonItem>

          <IonItem button onClick={() => setShowResetAlert(true)}>
            <IonIcon icon={refresh} slot="start" className="mr-3" />
            <IonLabel>Resetear liga</IonLabel>
          </IonItem>

          <IonItem button onClick={() => setShowEditFantasyLeagueConfig(true)}>
            <IonIcon icon={settingsOutline} slot="start" className="mr-3" />
            <IonLabel>Editar configuración de liga</IonLabel>
          </IonItem>

          <IonItem button onClick={() => setShowManageAdmins(true)}>
            <IonIcon icon={shieldCheckmarkOutline} slot="start" className="mr-3" />
            <IonLabel>Gestionar administradores</IonLabel>
          </IonItem>

          <IonItem button onClick={() => setShowExpelMember(true)}>
            <IonIcon icon={personRemoveOutline} slot="start" className="mr-3" />
            <IonLabel>Expulsar participante</IonLabel>
          </IonItem>

          <IonItem button onClick={() => setShowDeleteAlert(true)}>
            <IonIcon icon={trash} slot="start" className="mr-3" color="danger" />
            <IonLabel color="danger">Eliminar liga</IonLabel>
          </IonItem>
        </>
      )}

      <IonAlert
        isOpen={showResetAlert}
        onDidDismiss={() => setShowResetAlert(false)}
        header="Resetear liga"
        message="¿Estás seguro de que quieres resetear la liga? Se reasignarán los jugadores aleatoriamente y se eliminarán todas las puntuaciones y clasificaciones anteriores."
        buttons={[
          { text: 'Cancelar', role: 'cancel' },
          { text: 'Resetear', role: 'confirm', handler: handleResetTeams },
        ]}
      />

      <EditFantasyLeagueConfigModal
        isOpen={showEditFantasyLeagueConfig}
        loading={updateLoading || leagueLoading}
        initial={initialConfig}
        onClose={() => setShowEditFantasyLeagueConfig(false)}
        onSave={(next) =>
          handleUpdateFantasyLeague({
            name: next.name,
            maxPlayers: next.maxPlayers,
            isPublic: next.isPublic,
          })
        }
      />

      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header="Eliminar liga"
        message="¿Estás seguro de que quieres eliminar esta liga? Esta acción no se puede deshacer."
        buttons={[
          { text: 'Cancelar', role: 'cancel' },
          { text: 'Eliminar', role: 'confirm', handler: handleDeleteLeague },
        ]}
      />

      <SecretCodeModal
        isOpen={showSecretCode}
        leagueId={leagueId}
        onClose={() => setShowSecretCode(false)}
      />

      <ExpelMemberModal
        isOpen={showExpelMember}
        leagueId={leagueId}
        members={league?.members ?? []}
        onClose={() => setShowExpelMember(false)}
      />

      <ManageAdminsModal
        key={showManageAdmins ? 'manage-admins-open' : 'manage-admins-closed'}
        isOpen={showManageAdmins}
        leagueId={leagueId}
        members={league?.members ?? []}
        currentMemberId={currentMemberId}
        currentMemberRole={currentMemberRole}
        onClose={() => setShowManageAdmins(false)}
      />
    </>
  );
};
